import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from './Layout';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { AlertTriangle, Home, Users, Building2, CheckCircle, RefreshCw, X } from 'lucide-react';
import axios from 'axios';
import { API } from '../utils/config';


const DataMissingDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    properties: 0,
    partners: 0,
    managers: 0
  });
  const [propertiesMissing, setPropertiesMissing] = useState([]);
  const [partnersMissing, setPartnersMissing] = useState([]);
  const [managersMissing, setManagersMissing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState('');
  
  // Modal states
  const [showPropertyModal, setShowPropertyModal] = useState(false);
  const [showPartnerModal, setShowPartnerModal] = useState(false);
  const [showManagerModal, setShowManagerModal] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [editingPartner, setEditingPartner] = useState(null);
  const [editingManager, setEditingManager] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    // Get user role
    const user = JSON.parse(localStorage.getItem('hw_user') || '{}');
    setUserRole(user.role || '');
    fetchMissingData(user.role);
  }, []);

  const fetchMissingData = async (role) => {
    try {
      const token = localStorage.getItem('hw_token');
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch data based on role
      const requests = [];
      
      // Customer Service & Admin can see properties
      if (role === 'customer_service' || role === 'admin') {
        requests.push(axios.get(`${API}/api/missing-data/properties`, { headers }).catch(() => ({ data: [] })));
      } else {
        requests.push(Promise.resolve({ data: [] }));
      }

      // Marketing & Admin can see partners and managers
      if (role === 'marketing' || role === 'admin') {
        requests.push(axios.get(`${API}/api/missing-data/partners`, { headers }).catch(() => ({ data: [] })));
        requests.push(axios.get(`${API}/api/missing-data/managers`, { headers }).catch(() => ({ data: [] })));
      } else {
        requests.push(Promise.resolve({ data: [] }));
        requests.push(Promise.resolve({ data: [] }));
      }

      const [propsRes, partnersRes, managersRes] = await Promise.all(requests);

      setPropertiesMissing(propsRes.data || []);
      setPartnersMissing(partnersRes.data || []);
      setManagersMissing(managersRes.data || []);

      setStats({
        properties: propsRes.data?.length || 0,
        partners: partnersRes.data?.length || 0,
        managers: managersRes.data?.length || 0
      });

      setLoading(false);
    } catch (error) {
      console.error('Error fetching missing data:', error);
      setLoading(false);
    }
  };

  const showNotification = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const handleCompleteProperty = async (propertyId) => {
    try {
      const token = localStorage.getItem('hw_token');
      const response = await axios.get(`${API}/api/properties`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const property = response.data.find(p => p.id === propertyId);
      if (property) {
        setEditingProperty(property);
        setShowPropertyModal(true);
      }
    } catch (error) {
      console.error('Error fetching property:', error);
      showNotification('error', 'Errore nel caricamento dell\'immobile');
    }
  };

  const handleCompletePartner = async (partnerId) => {
    try {
      const token = localStorage.getItem('hw_token');
      const response = await axios.get(`${API}/api/partners`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const partner = response.data.find(p => p.id === partnerId);
      if (partner) {
        setEditingPartner(partner);
        setShowPartnerModal(true);
      }
    } catch (error) {
      console.error('Error fetching partner:', error);
      showNotification('error', 'Errore nel caricamento del partner');
    }
  };

  const handleCreateManager = (managerName) => {
    // Pre-compila il nome del manager da creare
    const nameParts = managerName.split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';
    
    setEditingManager({
      first_name: firstName,
      last_name: lastName,
      entity_type: 'private',
      phone: '',
      email: '',
      address: '',
      city: '',
      province: '',
      country: 'Italia',
      operation_area_city: '',
      operation_area_province: '',
      is_also_partner: false,
      notes: ''
    });
    setShowManagerModal(true);
  };

  const handleSaveProperty = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('hw_token');
      await axios.put(`${API}/api/properties/${editingProperty.id}`, editingProperty, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showNotification('success', 'Immobile aggiornato con successo');
      setShowPropertyModal(false);
      setEditingProperty(null);
      fetchMissingData(userRole);
    } catch (error) {
      console.error('Error updating property:', error);
      showNotification('error', 'Errore nell\'aggiornamento dell\'immobile');
    }
  };

  const handleSavePartner = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('hw_token');
      await axios.put(`${API}/api/partners/${editingPartner.id}`, editingPartner, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showNotification('success', 'Partner aggiornato con successo');
      setShowPartnerModal(false);
      setEditingPartner(null);
      fetchMissingData(userRole);
    } catch (error) {
      console.error('Error updating partner:', error);
      showNotification('error', 'Errore nell\'aggiornamento del partner');
    }
  };

  const handleSaveManager = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('hw_token');
      await axios.post(`${API}/api/property-managers`, editingManager, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showNotification('success', 'Responsabile creato con successo');
      setShowManagerModal(false);
      setEditingManager(null);
      fetchMissingData(userRole);
    } catch (error) {
      console.error('Error creating manager:', error);
      showNotification('error', 'Errore nella creazione del responsabile');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <AlertTriangle className="h-7 w-7 text-amber-600" />
            Dati Mancanti da Completare
          </h2>
          <p className="text-slate-600 mt-2">
            Panoramica dei dati da completare dall'importazione Excel
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          {(userRole === 'customer_service' || userRole === 'admin') && (
            <Card className="p-6 border-l-4 border-l-orange-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 font-medium">Immobili</p>
                  <p className="text-3xl font-bold text-orange-600">{stats.properties}</p>
                </div>
                <Home className="h-12 w-12 text-orange-200" />
              </div>
            </Card>
          )}

          {(userRole === 'marketing' || userRole === 'admin') && (
            <>
              <Card className="p-6 border-l-4 border-l-blue-500">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600 font-medium">Partner</p>
                    <p className="text-3xl font-bold text-blue-600">{stats.partners}</p>
                  </div>
                  <Building2 className="h-12 w-12 text-blue-200" />
                </div>
              </Card>

              <Card className="p-6 border-l-4 border-l-purple-500">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600 font-medium">Responsabili</p>
                    <p className="text-3xl font-bold text-purple-600">{stats.managers}</p>
                  </div>
                  <Users className="h-12 w-12 text-purple-200" />
                </div>
              </Card>
            </>
          )}
        </div>

        {/* Immobili con Dati Mancanti - Customer Service & Admin */}
        {(userRole === 'customer_service' || userRole === 'admin') && (
        <Card className="mb-6">
          <div className="p-6 border-b bg-gradient-to-r from-orange-50 to-white">
            <h3 className="text-xl font-semibold text-slate-800 flex items-center gap-2">
              <Home className="h-6 w-6 text-orange-600" />
              Immobili con Dati Mancanti ({stats.properties})
            </h3>
          </div>
          <div className="p-6">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Codice</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Nome</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Campi Mancanti</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">Azioni</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {propertiesMissing.slice(0, 10).map((item) => (
                    <tr key={item.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 text-sm font-mono">{item.property_code}</td>
                      <td className="px-4 py-3 text-sm">{item.property_name}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {item.missing_fields?.map((field) => (
                            <span key={field} className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded">
                              {field}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="text-blue-600 hover:bg-blue-50"
                          onClick={() => handleCompleteProperty(item.property_id)}
                        >
                          Completa
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Card>
        )}

        {/* Partner con Dati Mancanti - Marketing & Admin */}
        {(userRole === 'marketing' || userRole === 'admin') && (
        <Card className="mb-6">
          <div className="p-6 border-b bg-gradient-to-r from-blue-50 to-white">
            <h3 className="text-xl font-semibold text-slate-800 flex items-center gap-2">
              <Building2 className="h-6 w-6 text-blue-600" />
              Partner con Dati Mancanti ({stats.partners})
            </h3>
          </div>
          <div className="p-6">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Nome</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Campi Mancanti</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">Azioni</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {partnersMissing.map((item) => (
                    <tr key={item.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 text-sm font-medium">{item.name}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {item.missing_fields?.map((field) => (
                            <span key={field} className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">
                              {field}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="text-blue-600 hover:bg-blue-50"
                          onClick={() => handleCompletePartner(item.id)}
                        >
                          Completa
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Card>
        )}

        {/* Responsabili Mancanti - Marketing & Admin */}
        {(userRole === 'marketing' || userRole === 'admin') && (
        <Card>
          <div className="p-6 border-b bg-gradient-to-r from-purple-50 to-white">
            <h3 className="text-xl font-semibold text-slate-800 flex items-center gap-2">
              <Users className="h-6 w-6 text-purple-600" />
              Responsabili Mancanti ({stats.managers})
            </h3>
          </div>
          <div className="p-6">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Nome</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Immobile Riferimento</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">Azioni</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {managersMissing.map((item) => (
                    <tr key={item.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 text-sm font-medium">{item.full_name}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{item.source_property}</td>
                      <td className="px-4 py-3 text-center">
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="text-blue-600 hover:bg-blue-50"
                          onClick={() => handleCreateManager(item.full_name)}
                        >
                          Crea Responsabile
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Card>
        )}

        {/* Message notification */}
        {message.text && (
          <div className={`fixed bottom-4 right-4 p-4 rounded-lg shadow-lg ${
            message.type === 'success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
          }`}>
            <p className={message.type === 'success' ? 'text-green-800' : 'text-red-800'}>
              {message.text}
            </p>
          </div>
        )}

        {/* Property Edit Modal */}
        <Dialog open={showPropertyModal} onOpenChange={setShowPropertyModal}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Completa Dati Immobile</DialogTitle>
              <DialogDescription>
                Completa i dati mancanti per questo immobile
              </DialogDescription>
            </DialogHeader>
            {editingProperty && (
              <form onSubmit={handleSaveProperty} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Codice Immobile *</Label>
                    <Input
                      value={editingProperty.property_code}
                      disabled
                      className="bg-gray-50"
                    />
                  </div>
                  <div>
                    <Label>Nome Immobile *</Label>
                    <Input
                      value={editingProperty.name}
                      onChange={(e) => setEditingProperty({...editingProperty, name: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label>Indirizzo</Label>
                    <Input
                      value={editingProperty.address || ''}
                      onChange={(e) => setEditingProperty({...editingProperty, address: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Città</Label>
                    <Input
                      value={editingProperty.city || ''}
                      onChange={(e) => setEditingProperty({...editingProperty, city: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Provincia</Label>
                    <Input
                      value={editingProperty.province || ''}
                      onChange={(e) => setEditingProperty({...editingProperty, province: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>CAP</Label>
                    <Input
                      value={editingProperty.postal_code || ''}
                      onChange={(e) => setEditingProperty({...editingProperty, postal_code: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>CIN</Label>
                    <Input
                      value={editingProperty.cin || ''}
                      onChange={(e) => setEditingProperty({...editingProperty, cin: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>CIR</Label>
                    <Input
                      value={editingProperty.cir || ''}
                      onChange={(e) => setEditingProperty({...editingProperty, cir: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Budget Interventi (€)</Label>
                    <Input
                      type="number"
                      value={editingProperty.intervention_budget || ''}
                      onChange={(e) => setEditingProperty({...editingProperty, intervention_budget: parseFloat(e.target.value) || null})}
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowPropertyModal(false)}>
                    Annulla
                  </Button>
                  <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                    Salva
                  </Button>
                </div>
              </form>
            )}
          </DialogContent>
        </Dialog>

        {/* Partner Edit Modal */}
        <Dialog open={showPartnerModal} onOpenChange={setShowPartnerModal}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Completa Dati Partner</DialogTitle>
              <DialogDescription>
                Completa i dati mancanti per questo partner
              </DialogDescription>
            </DialogHeader>
            {editingPartner && (
              <form onSubmit={handleSavePartner} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label>Nome Partner *</Label>
                    <Input
                      value={editingPartner.name}
                      onChange={(e) => setEditingPartner({...editingPartner, name: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label>Tipo Entità</Label>
                    <Select
                      value={editingPartner.entity_type || 'private'}
                      onValueChange={(value) => setEditingPartner({...editingPartner, entity_type: value})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="private">Privato</SelectItem>
                        <SelectItem value="company">Azienda</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Telefono</Label>
                    <Input
                      value={editingPartner.phone || ''}
                      onChange={(e) => setEditingPartner({...editingPartner, phone: e.target.value})}
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={editingPartner.email || ''}
                      onChange={(e) => setEditingPartner({...editingPartner, email: e.target.value})}
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>Indirizzo</Label>
                    <Input
                      value={editingPartner.address || ''}
                      onChange={(e) => setEditingPartner({...editingPartner, address: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Città</Label>
                    <Input
                      value={editingPartner.city || ''}
                      onChange={(e) => setEditingPartner({...editingPartner, city: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Provincia</Label>
                    <Input
                      value={editingPartner.province || ''}
                      onChange={(e) => setEditingPartner({...editingPartner, province: e.target.value})}
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowPartnerModal(false)}>
                    Annulla
                  </Button>
                  <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                    Salva
                  </Button>
                </div>
              </form>
            )}
          </DialogContent>
        </Dialog>

        {/* Manager Create Modal */}
        <Dialog open={showManagerModal} onOpenChange={setShowManagerModal}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Crea Nuovo Responsabile</DialogTitle>
              <DialogDescription>
                Inserisci i dati del nuovo responsabile
              </DialogDescription>
            </DialogHeader>
            {editingManager && (
              <form onSubmit={handleSaveManager} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Nome *</Label>
                    <Input
                      value={editingManager.first_name}
                      onChange={(e) => setEditingManager({...editingManager, first_name: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label>Cognome *</Label>
                    <Input
                      value={editingManager.last_name}
                      onChange={(e) => setEditingManager({...editingManager, last_name: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label>Tipo Entità</Label>
                    <Select
                      value={editingManager.entity_type || 'private'}
                      onValueChange={(value) => setEditingManager({...editingManager, entity_type: value})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="private">Privato</SelectItem>
                        <SelectItem value="company">Azienda</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Telefono</Label>
                    <Input
                      value={editingManager.phone || ''}
                      onChange={(e) => setEditingManager({...editingManager, phone: e.target.value})}
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={editingManager.email || ''}
                      onChange={(e) => setEditingManager({...editingManager, email: e.target.value})}
                      required
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>Indirizzo</Label>
                    <Input
                      value={editingManager.address || ''}
                      onChange={(e) => setEditingManager({...editingManager, address: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Città</Label>
                    <Input
                      value={editingManager.city || ''}
                      onChange={(e) => setEditingManager({...editingManager, city: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Provincia</Label>
                    <Input
                      value={editingManager.province || ''}
                      onChange={(e) => setEditingManager({...editingManager, province: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Area Operativa - Città</Label>
                    <Input
                      value={editingManager.operation_area_city || ''}
                      onChange={(e) => setEditingManager({...editingManager, operation_area_city: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Area Operativa - Provincia</Label>
                    <Input
                      value={editingManager.operation_area_province || ''}
                      onChange={(e) => setEditingManager({...editingManager, operation_area_province: e.target.value})}
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowManagerModal(false)}>
                    Annulla
                  </Button>
                  <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                    Crea
                  </Button>
                </div>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default DataMissingDashboard;
